import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Rating,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { EmptyState } from "@lopie/kernel-ui";

import {
  ISSUE_STATUS,
  Msg,
  RatingLine,
  STATUS,
  URGENCY,
  fmtDate,
  fmtWhen,
  groupBy,
  money,
  overlaps,
  plural,
  quote,
  useRun,
} from "./shared.jsx";

const dateSlot = { inputLabel: { shrink: true } };

export default function GuestView({ state, act }) {
  const live = state.bookings.some((b) => ["approved", "checked_in"].includes(b.status));
  const [tab, setTab] = useState(live ? 1 : 0);
  return (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Book a stay" />
        <Tab label={`My stays (${state.bookings.length})`} />
        <Tab label="Guest ratings" />
      </Tabs>
      {tab === 0 ? <Book state={state} act={act} /> : null}
      {tab === 1 ? <MyStays state={state} act={act} /> : null}
      {tab === 2 ? <Ratings reviews={state.reviews} /> : null}
    </Stack>
  );
}

function Book({ state, act }) {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  if (!state.units.length) {
    return (
      <EmptyState title="No places to book yet">
        <Typography variant="body2">
          The property manager has not listed any rentals or campsites yet.
        </Typography>
      </EmptyState>
    );
  }
  const units = state.units.filter((u) => filter === "all" || u.category === filter);
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Pick a place, choose your dates and send a request. The property manager
        approves each booking; you will get an email either way.
      </Typography>
      <TextField
        select
        size="small"
        label="Show"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ maxWidth: 240 }}
      >
        <MenuItem value="all">Everything</MenuItem>
        <MenuItem value="rental">Rentals</MenuItem>
        <MenuItem value="campsite">Campsites</MenuItem>
      </TextField>
      {groupBy(units, (u) => u.property).map(([property, list]) => (
        <Box key={property}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {property}
          </Typography>
          <Stack spacing={1.5}>
            {list.map((u) => (
              <UnitCard
                key={u.id}
                unit={u}
                me={state.me}
                act={act}
                open={open === u.id}
                onToggle={() => setOpen(open === u.id ? null : u.id)}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function unitFacts(u) {
  const bits = [u.category === "campsite" ? "Campsite" : "Rental"];
  if (u.sleeps) bits.push(`sleeps ${u.sleeps}`);
  if (u.site_type) bits.push(u.site_type);
  if (u.hookups) bits.push(`hookups: ${u.hookups}`);
  return bits.join(" · ");
}

function UnitCard({ unit, me, act, open, onToggle }) {
  const { run, busy, msg } = useRun(act);
  const [f, setF] = useState({
    start: "",
    end: "",
    guest_name: "",
    email: (me.username || "").includes("@") ? me.username : "",
    phone: "",
    party_size: 1,
    note: "",
  });
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const q = f.start && f.end && f.end > f.start ? quote(unit, f.start, f.end) : null;
  const clash = q ? (unit.taken || []).find((r) => overlaps(f.start, f.end, r.start, r.end)) : null;
  const tooShort = q && q.nights < (unit.min_nights || 1);
  const past = f.start && f.start < me.today;
  const upcoming = (unit.taken || []).filter((r) => r.end > me.today).slice(0, 6);

  const submit = async () => {
    const res = await run(
      "request_booking",
      { ...f, unit_id: unit.id, party_size: Number(f.party_size) || 1 },
      "Request sent. These dates are held for you while the manager decides. Find it under My stays.",
    );
    if (res) setF((v) => ({ ...v, start: "", end: "", note: "" }));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h3">{unit.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {unitFacts(unit)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: { sm: "right" } }}>
          <Typography variant="body1">{money(unit.price)} a night</Typography>
          <Typography variant="caption" color="text.secondary">
            Minimum stay {plural(unit.min_nights || 1, "night")}
          </Typography>
        </Box>
      </Stack>
      {unit.description ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {unit.description}
        </Typography>
      ) : null}
      {(unit.seasons || []).length ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Seasonal rates:{" "}
          {unit.seasons
            .map((s) => `${s.name ? `${s.name} ` : ""}${fmtDate(s.start)} to ${fmtDate(s.end)} at ${money(s.price)} a night`)
            .join("; ")}
        </Typography>
      ) : null}
      <Box sx={{ mt: 1 }}>
        <RatingLine summary={unit.rating} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {upcoming.length
          ? `Already taken (arrive → leave): ${upcoming.map((r) => `${fmtDate(r.start)} → ${fmtDate(r.end)}`).join("; ")}`
          : "No upcoming dates are taken."}
      </Typography>
      <Box sx={{ mt: 1.5 }}>
        <Button variant={open ? "text" : "outlined"} onClick={onToggle}>
          {open ? "Close" : "Choose dates"}
        </Button>
      </Box>
      {open ? (
        <Stack spacing={1.5} sx={{ mt: 2, maxWidth: "56ch" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField label="Arrive" type="date" size="small" value={f.start} onChange={set("start")} slotProps={dateSlot} />
            <TextField label="Leave" type="date" size="small" value={f.end} onChange={set("end")} slotProps={dateSlot} />
            <TextField label="Guests" type="number" size="small" value={f.party_size} onChange={set("party_size")} sx={{ maxWidth: 110 }} />
          </Stack>
          <TextField label="Your name" size="small" value={f.guest_name} onChange={set("guest_name")} />
          <TextField label="Email for your confirmation" type="email" size="small" value={f.email} onChange={set("email")} />
          <TextField label="Mobile (optional)" type="tel" size="small" value={f.phone} onChange={set("phone")} />
          <TextField label="Anything the manager should know (optional)" size="small" multiline minRows={2} value={f.note} onChange={set("note")} />
          {q ? (
            <Typography variant="body2">
              {plural(q.nights, "night")} · {money(q.total)} total{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                (an estimate; the server works out the price again when you send)
              </Typography>
            </Typography>
          ) : null}
          {past ? <Typography variant="body2" role="alert">Your arrival date has already passed.</Typography> : null}
          {clash ? <Typography variant="body2" role="alert">Those dates overlap dates that are already taken.</Typography> : null}
          {tooShort ? (
            <Typography variant="body2" role="alert">
              This place has a minimum stay of {plural(unit.min_nights, "night")}.
            </Typography>
          ) : null}
          <Box>
            <Button variant="contained" disabled={busy || !q} onClick={submit}>
              Request booking
            </Button>
          </Box>
          <Msg msg={msg} />
        </Stack>
      ) : null}
    </Paper>
  );
}

function MyStays({ state, act }) {
  if (!state.bookings.length) {
    return (
      <EmptyState title="You have no stays yet">
        <Typography variant="body2">
          Book a stay and it will appear here, with check-in, maintenance
          reports and feedback for it.
        </Typography>
      </EmptyState>
    );
  }
  const sorted = [...state.bookings].sort((a, b) => (a.start < b.start ? 1 : -1));
  return (
    <Stack spacing={2}>
      {sorted.map((b) => (
        <Stay key={b.id} b={b} state={state} act={act} />
      ))}
    </Stack>
  );
}

function Stay({ b, state, act }) {
  const { run, busy, msg } = useRun(act);
  const [accept, setAccept] = useState(false);
  const [issue, setIssue] = useState({ description: "", urgency: "normal" });
  const [rev, setRev] = useState({ rating: 0, comment: "" });
  const today = state.me.today;
  const unit = state.units.find((u) => u.id === b.unit_id);
  const canCheckIn = b.status === "approved" && today >= b.start && today <= b.end;
  const early = b.status === "approved" && today < b.start;
  const missed = b.status === "approved" && today > b.end;
  const canCancel = ["requested", "approved"].includes(b.status) && today < b.start;
  const canReport = ["approved", "checked_in", "checked_out"].includes(b.status);
  const canReview = ["checked_in", "checked_out"].includes(b.status) && !b.my_review;
  const issues = state.issues.filter((i) => i.booking_id === b.id);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h3">{b.unit_name}</Typography>
          <Typography variant="body2">
            Arrive {fmtDate(b.start)} · leave {fmtDate(b.end)} · {plural(b.nights, "night")} · {money(b.total)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Payment: {b.paid ? "marked paid by the manager" : "not marked paid yet"}
          </Typography>
        </Box>
        <Chip variant="outlined" label={STATUS[b.status] || b.status} />
      </Stack>

      {b.status === "requested" ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          The manager has not decided yet. Your dates are held until they do.
        </Typography>
      ) : null}

      {b.arrival_info ? (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          <strong>Getting there:</strong> {b.arrival_info}
        </Typography>
      ) : null}

      {early ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Check-in opens on {fmtDate(b.start)}. Your door code and wifi details
          appear here once you check in.
        </Typography>
      ) : null}
      {missed ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          This stay&apos;s dates have passed without a check-in.
        </Typography>
      ) : null}

      {canCheckIn ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h3">Check in</Typography>
          <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
            {unit?.house_rules || "No house rules have been written for this place."}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={accept} onChange={(e) => setAccept(e.target.checked)} />}
            label="I have read and accept the house rules"
          />
          <Box>
            <Button
              variant="contained"
              disabled={busy || !accept}
              onClick={() => run("check_in", { id: b.id, accept_rules: accept }, "You are checked in. Your access details are below.")}
            >
              I&apos;ve arrived — check in
            </Button>
          </Box>
        </Box>
      ) : null}

      {b.status === "checked_in" ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h3">Your access details</Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="body1">Door code: {b.access?.door_code || "none set, ask the manager"}</Typography>
            <Typography variant="body1">Wifi network: {b.access?.wifi_name || "none set"}</Typography>
            <Typography variant="body1">Wifi password: {b.access?.wifi_password || "none set"}</Typography>
          </Stack>
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" disabled={busy} onClick={() => run("check_out", { id: b.id }, "You are checked out. Thanks for staying!")}>
              Check out
            </Button>
          </Box>
        </Box>
      ) : null}

      {canCancel ? (
        <Box sx={{ mt: 1.5 }}>
          <Button variant="text" disabled={busy} onClick={() => run("cancel", { id: b.id }, "Booking cancelled and the dates released.")}>
            Cancel this booking
          </Button>
        </Box>
      ) : null}

      {canReport ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h3">Report a maintenance issue</Typography>
          <Stack spacing={1.5} sx={{ mt: 1, maxWidth: "56ch" }}>
            <TextField
              label="What is wrong?"
              size="small"
              multiline
              minRows={2}
              value={issue.description}
              onChange={(e) => setIssue((v) => ({ ...v, description: e.target.value }))}
            />
            <TextField
              select
              size="small"
              label="How urgent?"
              value={issue.urgency}
              onChange={(e) => setIssue((v) => ({ ...v, urgency: e.target.value }))}
              sx={{ maxWidth: 220 }}
            >
              <MenuItem value="low">Low: whenever convenient</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="urgent">Urgent: needs attention now</MenuItem>
            </TextField>
            <Box>
              <Button
                variant="outlined"
                disabled={busy || !issue.description.trim()}
                onClick={async () => {
                  const res = await run("report_issue", { booking_id: b.id, ...issue }, "Reported to the manager.");
                  if (res) setIssue({ description: "", urgency: "normal" });
                }}
              >
                Send report
              </Button>
            </Box>
          </Stack>
          {issues.length ? (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {issues.map((i) => (
                <Typography variant="body2" key={i.id}>
                  {fmtWhen(i.created_at)} · {URGENCY[i.urgency]} · {ISSUE_STATUS[i.status] || i.status}: {i.description}
                  {(i.history || []).filter((h) => h.note).slice(-1).map((h) => ` (manager: ${h.note})`)}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {canReview ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h3">Leave feedback</Typography>
          <Typography variant="body2" color="text.secondary">
            Your rating, comment and first name are shown to other guests.
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1, maxWidth: "56ch" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Rating
                name={`rating-${b.id}`}
                value={rev.rating}
                onChange={(_, v) => setRev((x) => ({ ...x, rating: v || 0 }))}
              />
              <Typography variant="body2">{rev.rating ? `${rev.rating} of 5` : "Choose 1 to 5 stars"}</Typography>
            </Stack>
            <TextField
              label="Comment"
              size="small"
              multiline
              minRows={2}
              value={rev.comment}
              onChange={(e) => setRev((x) => ({ ...x, comment: e.target.value }))}
            />
            <Box>
              <Button
                variant="outlined"
                disabled={busy || !rev.rating}
                onClick={() => run("review", { booking_id: b.id, ...rev }, "Thanks for your feedback.")}
              >
                Send feedback
              </Button>
            </Box>
          </Stack>
        </Box>
      ) : null}
      {b.my_review ? (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          You rated this stay {b.my_review.rating} of 5.
        </Typography>
      ) : null}

      <Box sx={{ mt: 1.5 }}>
        <Msg msg={msg} />
      </Box>
    </Paper>
  );
}

function Ratings({ reviews }) {
  if (!reviews.length) {
    return (
      <EmptyState title="No ratings published yet">
        <Typography variant="body2">Guests can rate a stay once they have checked in.</Typography>
      </EmptyState>
    );
  }
  const sorted = [...reviews].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  return (
    <Stack spacing={1.5}>
      {sorted.map((r) => (
        <Paper variant="outlined" sx={{ p: 2 }} key={r.id}>
          <Typography variant="h3">{r.unit_name}</Typography>
          <RatingLine summary={{ avg: r.rating, count: 1 }} />
          {r.comment ? <Typography variant="body2" sx={{ mt: 0.5 }}>{r.comment}</Typography> : null}
          <Typography variant="caption" color="text.secondary">
            {r.guest_first || "A guest"} · {fmtWhen(r.created_at)}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
}
